import GroupedCombobox from '@/components/grouped-combobox/GroupedCombobox';
import useGroupedCombobox from '@/components/grouped-combobox/useGroupedCombobox';
import { BreadcrumbRouterLink, RouterLink } from '@/components/link/Link';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import useSearchQuery from '@/hooks/useSearchQuery';
import { Optional } from '@/utils/types';
import { useLoaderData, useNavigate, useNavigation, useParams, useSearchParams } from 'react-router';
import { labelsByType, queryDashboardInfo, searchDevicesPredicate } from './constants';
import { DashboardInfoResponse, DeviceLoaderResponse, type DeviceMetadata, DeviceResponse, DeviceStats } from './types';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '@/components/ui/breadcrumb';

import { TargetIcon } from '@radix-ui/react-icons';
import UIError from '@/errors/UIError';
import { decodeDateFromURL, DEFAULT_DATE_TIME_FORMAT, encodeDateToURL } from '@/utils/dates';
import { addDays, format, subDays } from 'date-fns';
import { ROUTES } from '@/constants';
import ErrorAlert from '@/components/error-alert/ErrorAlert';
import { useState } from 'react';
import { SwapDeviceForm } from './swap-device/SwapDeviceForm';
import LineChart, { ChartSeries } from '@/components/charts/line-chart/LineChart';
import DateRangePicker from '@/components/date-range-picker/DateRangePicker';
import { DateRange } from 'react-day-picker';
import { mergeQueryParams } from '@/utils/merge-query-params';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { extractErrorData } from '@/utils/errors';
import Spinner from '@/components/spinner/Spinner';
import { SkeletonCard } from '@/components/skeleton-card/SkeletonCard';
import UndeployDevice from './deploy-device/UndeployDevice';
import { UpdateSendingIntervalForm } from '@/routes/device/update-sending-interval/UpdateSendingInterval.tsx';
import { getSendingInterval } from './constants';
import useTitle from '@/hooks/useTitle.ts';

function appendSearchQueryParamIfPresent(url: string, searchQuery: Optional<string>): string {
	let auxUrl = url;

	if (searchQuery && searchQuery.length > 0) {
		auxUrl += '?' + new URLSearchParams({ s: searchQuery }).toString();
	}

	return auxUrl;
}

type DataCardProps = {
	title: string;
	value: number;
	datetime: string;
};

type DeviceAdditionalInfo = {
	deviceID: string;
};

function makeInternalDeviceID(device: DeviceResponse): string {
	return `${device.site?.trim()}$$${device.id}$$${device.landowner?.trim()}`;
}

function DataCard(props: DataCardProps) {
	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
				<CardTitle className="text-sm font-medium">{props.title}</CardTitle>
			</CardHeader>
			<CardContent>
				<div>
					<span className="font-medium text-sm">Last value:</span>
					<span className="text-2xl ml-2 font-bold">{props.value}</span>
				</div>
				<p className="text-xs text-muted-foreground">{props.datetime}</p>
			</CardContent>
		</Card>
	);
}

function Device() {
	const { deviceId } = useParams();
	const deviceIdNumber = parseInt(deviceId ?? '');

	const navigate = useNavigate();
	const navigation = useNavigation();
	const isNavigating = !!navigation.location;

	const [searchParams, setSearchParams] = useSearchParams();

	const fromDateParam = searchParams.get('from') ?? '';
	const from = decodeDateFromURL(fromDateParam);

	const toDateParam = searchParams.get('to') ?? '';
	const to = decodeDateFromURL(toDateParam);

	const routeData = useLoaderData() as DeviceLoaderResponse;

	const { searchQuery, handleSearch } = useSearchQuery();

	const { selectedItem, items } = useGroupedCombobox<DeviceResponse, DeviceAdditionalInfo>({
		data: routeData.devices.data,
		selectedItemPredicate(item) {
			return item.metadata?.deviceID === deviceId;
		},
		mapper: d => ({
			id: makeInternalDeviceID(d),
			name: d.landowner ? `${d.id.toString()} - ${d.landowner}` : d.id.toString(),
			parentId: d.site,
			metadata: {
				deviceID: d.id.toString()
			}
		}),
		searchPredicate: searchDevicesPredicate,
		searchQuery,
		selectedId: deviceId
	});

	useTitle(selectedItem?.name ?? 'Device Details');

	const { data: intervalData } = useQuery({
		queryKey: ['v1', 'devices', deviceId, 'sending-interval'],
		queryFn: () => getSendingInterval(deviceId!).queryFn()
	});

	const currentInterval = intervalData?.data?.updateManifestId;

	const dashboardQuery = useQuery({
		...queryDashboardInfo(deviceIdNumber, {
			startDate: fromDateParam,
			endDate: toDateParam,
			includeGainDistribution: 'false'
		}),
		placeholderData: keepPreviousData
	});

	const selectedDevice = routeData.devices.data.find(el => el.id.toString() === deviceId);

	const [isSwapFormOpen, setIsSwapFormOpen] = useState(false);
	const [isSendingIntervalFormOpen, setIsSendingIntervalFormOpen] = useState(false);
	const handleToggleSwapForm = () => {
		setIsSwapFormOpen(!isSwapFormOpen);
	};
	const handleToggleSendingIntervalForm = () => {
		setIsSendingIntervalFormOpen(!isSendingIntervalFormOpen);
	};

	const combobox = (
		<GroupedCombobox
			placeholder="Select device"
			label="Select device"
			orphanGroupName="Undeployed"
			onSearch={handleSearch}
			defaultSearchValue={searchQuery ?? undefined}
			selectedItem={selectedItem}
			onSelect={selectedItem => {
				if (!selectedItem.metadata) {
					throw new UIError('Unexpected state. Device has no metadata set.');
				}
				const url = appendSearchQueryParamIfPresent(`/devices/${selectedItem.metadata.deviceID}`, searchQuery);
				navigate(url);
			}}
			items={items}
		/>
	);

	const onDateRangeSelected = (dateRange: DateRange | undefined) => {
		if ((dateRange?.from && !dateRange?.to) || (dateRange?.to && !dateRange?.from)) {
			return;
		}

		const updated = mergeQueryParams(searchParams, {
			from: encodeDateToURL(dateRange?.from),
			to: encodeDateToURL(dateRange?.to)
		});

		setSearchParams(updated);
	};

	const onDateRangeReset = () => {
		setSearchParams(mergeQueryParams(searchParams, { from: undefined, to: undefined }));
	};

	function prepareSlowMonitoringSeriesData(data: DashboardInfoResponse['slowMonitoringSeries']): ChartSeries[] {
		return data.map(series => {
			if (series.name === 'recent_packets') {
				return {
					name: series.name,
					data: series.data.map(d => ({ x: d.timestamp, y: d.value })),
					type: 'scatter',
					showSymbol: true,
					symbolSize: 7,
					color: '#22c55e'
				};
			}

			if (series.name === 'bad_ms_packets') {
				return {
					name: series.name,
					data: series.data.map(d => ({ x: d.timestamp, y: d.value })),
					type: 'scatter',
					showSymbol: true,
					symbolSize: 7,
					color: '#ef4444'
				};
			}

			return {
				name: series.name,
				data: series.data.map(d => ({ x: d.timestamp, y: d.value }))
			};
		});
	}

	const dashboardInfoLoading = dashboardQuery.isLoading || dashboardQuery.isFetching;

	const initiallyVisibleSeries = ['sla_voltage', 'solar_voltage', 'rssi', 'nav_sat', 'recent_packets', 'bad_ms_packets'];

	return (
		<div className="flex flex-col gap-10">
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbRouterLink to={ROUTES.home}>Home</BreadcrumbRouterLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbRouterLink to="#">Devices</BreadcrumbRouterLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>{deviceId}</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			{!selectedDevice && (
				<div className="flex justify-between gap-10 flex-col md:flex-row">
					<ErrorAlert
						title="Device not found"
						description={
							<span>
								Device with id: <span className="font-bold">{deviceId}</span> was not found
							</span>
						}
					/>
					<div className="order-1 md:order-2">{combobox}</div>
				</div>
			)}

			{selectedDevice && (
				<>
					<div className="mb-3 w-full flex justify-between gap-2">
						<RouterLink
							className="bg-slate-200 dark:bg-slate-800 p-2 rounded text-xs items-center justify-center gap-2 flex font-medium hover:text-gray-500 dark:hover:text-gray-300"
							to={`/map?lng=${selectedDevice.longitude}&lat=${selectedDevice.latitude}&zoom=10&deviceId=${deviceId}`}
						>
							<TargetIcon />
							Go to Deployment Map
						</RouterLink>
						<div className="flex flex-col md:flex-row gap-2 items-center">
							{selectedDevice.id && <UndeployDevice deviceId={selectedDevice.id} />}
							<SwapDeviceForm
								isOpen={isSwapFormOpen}
								handleToggleForm={handleToggleSwapForm}
								isDisabled={isNavigating || selectedDevice.siteId === 0}
							/>
							<UpdateSendingIntervalForm
								isOpen={isSendingIntervalFormOpen}
								handleToggleForm={handleToggleSendingIntervalForm}
								currentInterval={currentInterval ?? 0}
							/>
						</div>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2">
						<div className="order-2 md:order-1">
							<div className="space-y-2">
								<div className="text-lg text-gray-800 dark:text-gray-200">
									<span className="font-semibold mr-2">Device:</span>
									{deviceId}
								</div>
								<div className="text-sm text-gray-800 dark:text-gray-200">
									<span className="font-medium mr-2">Site:</span>
									{selectedDevice?.site ?? 'Not deployed'}
								</div>
								<div className="text-sm text-gray-800 dark:text-gray-200">
									<span className="font-medium mr-2">Landowner:</span>
									{selectedDevice?.landowner ?? 'Not specified'}
								</div>
							</div>
						</div>

						<div className="order-1 mb-6 md:mb-0 md:ml-auto md:order-2">{combobox}</div>
					</div>

					{!dashboardInfoLoading && dashboardQuery.data?.data.stats && (
						<>
							<DeviceStatsCards className="mb-10" stats={dashboardQuery.data.data.stats} />
							<DeviceMetadata className="mb-10" metadata={dashboardQuery.data.data.metadata} />
						</>
					)}

					{dashboardInfoLoading && <SkeletonCard />}

					<div>
						<h4 className="text-md mb-2 text-gray-800 dark:text-gray-200 font-semibold">
							Last 10 data packets
						</h4>

						<div>
							{routeData.recentData.data.items.length === 0 ? (
								<Alert>
									<AlertDescription>There hasn't been a packet sent in over 1 hour.</AlertDescription>
								</Alert>
							) : (
								<Table>
									<TableCaption>
										If the date is in the year 2000 the device is only sending default data.
									</TableCaption>

									<TableHeader>
										<TableRow>
											<TableHead>Recent Date Time</TableHead>
											<TableHead>Data Packet Number</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{routeData.recentData.data.items.map(d => (
											<TableRow key={d.dataPacketNumber}>
												<TableCell>{format(d.realTime, DEFAULT_DATE_TIME_FORMAT)}</TableCell>
												<TableCell>{d.dataPacketNumber}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</div>
					</div>
					<div>
						<h4 className="text-md mb-2 text-gray-800 dark:text-gray-200 font-semibold">
							Slow monitoring data
						</h4>

						{(!routeData.slowMonitoring.data || routeData.slowMonitoring.data.length === 0) && (
							<Alert>
								<AlertDescription>No data available.</AlertDescription>
							</Alert>
						)}

						{routeData.slowMonitoring.data?.length > 0 && (
							<div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
								{routeData.slowMonitoring.data.map(d => (
									<DataCard
										key={d.type}
										title={labelsByType[d.type]}
										value={d.lastValue}
										datetime={format(d.realTime, DEFAULT_DATE_TIME_FORMAT)}
									/>
								))}
							</div>
						)}
					</div>

					<div className="flex gap-5 items-center">
						<DateRangePicker
							className="mb-2 w-fit"
							onSelect={onDateRangeSelected}
							onClear={onDateRangeReset}
							defaultValue={{
								from: from ?? subDays(new Date(), 3),
								to: to ?? addDays(new Date(), 1)
							}}
							label="Date Range"
						/>
						{dashboardInfoLoading && <Spinner size={26} className="mt-6" />}
					</div>

					{dashboardQuery.data?.data && (
						<div
							className={cn({
								'opacity-50 pointer-events-none': dashboardInfoLoading
							})}
						>
							<LineChart
								series={prepareSlowMonitoringSeriesData(dashboardQuery.data.data.slowMonitoringSeries)}
								initiallyVisibleSeries={initiallyVisibleSeries}
								fillGaps={true}
								resetKey={`${deviceIdNumber}:${fromDateParam}:${toDateParam}`}
							/>
						</div>
					)}

					{dashboardQuery.error && (
						<ErrorAlert
							title="Error fetching slow monitoring data"
							description={<span>{extractErrorData(dashboardQuery.error).errorMessage}</span>}
						/>
					)}
				</>
			)}
		</div>
	);
}

type DeviceCardProps = {
	title: string;
	value: string | null;
};

function DeviceCard({ title, value }: DeviceCardProps) {
	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
				<CardTitle className="text-sm font-medium">{title}</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="text-lg text-slate-800 font-semibold">{value || 'N/A'}</div>
			</CardContent>
		</Card>
	);
}

type DeviceMetadataProps = {
	className?: string;
	metadata: DeviceMetadata;
};

function DeviceMetadata({ className, metadata }: DeviceMetadataProps) {
	return (
		<div className={className}>
			<h4 className="text-md mb-2 text-gray-800 dark:text-gray-200 font-semibold">Metadata</h4>
			<div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
				<DeviceCard title="Trace Type" value={metadata.traceType} />
				<DeviceCard title="Trace Site" value={metadata.traceSite} />
				<DeviceCard title="Enclosure" value={metadata.enclosure} />
				<DeviceCard title="Antenna Type" value={metadata.antennaType} />
				<DeviceCard title="PV Panel Type" value={metadata.pvPanelType} />
			</div>
		</div>
	);
}

type DeviceStatsProps = {
	className?: string;
	stats: DeviceStats;
};

export function DeviceStatsCards({ className, stats }: DeviceStatsProps) {
	return (
		<div className={className}>
			<h4 className="text-md mb-2 text-gray-800 dark:text-gray-200 font-semibold">Statistics</h4>
			<div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
				<DeviceCard title="12-Hour Performance" value={`${stats.performance12Hour}%`} />
				<DeviceCard title="Last Packet" value={stats.lastPacket} />
				<DeviceCard title="Firmware" value={stats.firmware} />
				<DeviceCard title="Interval" value={stats.interval} />
				<DeviceCard title="Modem" value={stats.modem} />
			</div>
		</div>
	);
}

export default Device;
